import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Spinner, Alert, Form, Row, Col, ProgressBar } from 'react-bootstrap';
import { createPartnerRequest, resetCreatePartner } from '../../store/actions/partnerActions';
import { FiArrowLeft, FiLink, FiCheckCircle, FiFolder } from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminPartner.css';

const NAME_PATTERN = /^[A-Za-z\u00C0-\u024F\s'-]+$/;
/** Avec country_code : uniquement chiffres (l’indicatif est dans country_code). */
const PHONE_DIGITS_ONLY = /^\d{8,15}$/;
/** Sans country_code : « + » obligatoire puis 8 à 15 chiffres (numéro international). */
const PHONE_WITH_PLUS = /^\+\d{8,15}$/;

const PartnerCreateSuccessResult = ({ result, onBackToList }) => {
    const s3Folder = result?.s3_folder || {};
    const expectedDocuments = Array.isArray(s3Folder.expected_documents)
        ? s3Folder.expected_documents
        : [];

    return (
        <Card className="partner-create-success mb-4">
            <Card.Body>
                <div className="d-flex align-items-start gap-3 mb-3">
                    <FiCheckCircle size={28} className="text-success flex-shrink-0 mt-1" aria-hidden />
                    <div>
                        <h3 className="h5 mb-2">Compte partenaire créé avec succès</h3>
                        {result?.message && (
                            <div className="partner-create-success__message text-break">
                                {result.message}
                            </div>
                        )}
                    </div>
                </div>

                {(s3Folder.s3_uri || s3Folder.prefix || expectedDocuments.length > 0) && (
                    <div className="partner-create-success__s3 border rounded p-3 mb-3">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <FiFolder aria-hidden />
                            <h4 className="h6 mb-0">Dossier S3 — documents contractuels</h4>
                        </div>
                        {s3Folder.company_label && (
                            <p className="small mb-2">
                                <strong>Libellé :</strong> {s3Folder.company_label}
                            </p>
                        )}
                        {s3Folder.s3_uri && (
                            <p className="small mb-2 font-monospace text-break">
                                <strong>URI :</strong> {s3Folder.s3_uri}
                            </p>
                        )}
                        {(s3Folder.bucket || s3Folder.prefix) && (
                            <p className="small mb-2 text-muted">
                                {s3Folder.bucket && <>Bucket : <code>{s3Folder.bucket}</code></>}
                                {s3Folder.bucket && s3Folder.prefix && ' — '}
                                {s3Folder.prefix && <>Préfixe : <code>{s3Folder.prefix}</code></>}
                            </p>
                        )}
                        {expectedDocuments.length > 0 && (
                            <>
                                <div className="small fw-semibold mb-1">Documents à déposer :</div>
                                <ul className="small mb-0 ps-3">
                                    {expectedDocuments.map((doc) => (
                                        <li key={doc}><code>{doc}</code></li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>
                )}

                <div className="d-flex flex-wrap gap-2">
                    <Button variant="primary" onClick={onBackToList}>
                        Retour à la liste des partenaires
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );
};

const initialForm = {
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    user_address: '',
    city: '',
    zip_code: '',
    country: '',
    country_code: '',
    role: 'Partner',
    commission: '',
    legal_info: '',
    logo_url: '',
    contract_doc_s3_link: '',
};

const AdminPartnerCreate = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { create } = useSelector((state) => state.partners);

    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState(initialForm);
    const [pageError, setPageError] = useState('');

    useEffect(() => {
        dispatch(resetCreatePartner());
    }, [dispatch]);

    const handleBackToList = () => {
        dispatch(resetCreatePartner());
        navigate('/admin/partner');
    };

    const isCreateSuccess = create.success && create.data;

    const resetForm = () => {
        setFormData(initialForm);
        setStep(1);
        setPageError('');
    };

    const validateStep = (s) => {
        const t = (v) => (v || '').trim();
        if (s === 1) {
            if (t(formData.first_name).length < 2 || t(formData.first_name).length > 100) {
                return 'Le prénom doit faire entre 2 et 100 caractères.';
            }
            if (!NAME_PATTERN.test(t(formData.first_name))) {
                return 'Le prénom : uniquement lettres, espaces, tirets ou apostrophes.';
            }
            if (t(formData.last_name).length < 2 || t(formData.last_name).length > 100) {
                return 'Le nom doit faire entre 2 et 100 caractères.';
            }
            if (!NAME_PATTERN.test(t(formData.last_name))) {
                return 'Le nom : uniquement lettres, espaces, tirets ou apostrophes.';
            }
            if (t(formData.company_name).length < 2 || t(formData.company_name).length > 50) {
                return 'La société doit faire entre 2 et 50 caractères.';
            }
            const cc = t(formData.country_code);
            if (cc.length > 3) {
                return 'Le code pays ne peut pas dépasser 3 caractères.';
            }
            const phone = t(formData.phone_number);
            if (cc !== '' && phone === '') {
                return 'Le téléphone est obligatoire lorsque le code pays est renseigné.';
            }
            if (phone !== '' && cc !== '' && !PHONE_DIGITS_ONLY.test(phone)) {
                return 'Avec un code pays : saisissez uniquement 8 à 15 chiffres (sans « + » ni préfixe pays dans le numéro).';
            }
            if (phone !== '' && cc === '' && !PHONE_WITH_PLUS.test(phone)) {
                return 'Sans code pays : le numéro doit commencer par « + » puis 8 à 15 chiffres (ex. +33123456789).';
            }
            if (!['Partner', 'Orakle'].includes(formData.role)) return 'Rôle invalide.';
            return '';
        }
        if (s === 2) {
            if (t(formData.user_address).length < 2 || t(formData.user_address).length > 100) {
                return 'L’adresse doit faire entre 2 et 100 caractères.';
            }
            if (t(formData.city).length < 2 || t(formData.city).length > 100) {
                return 'La ville doit faire entre 2 et 100 caractères.';
            }
            if (t(formData.country).length < 2 || t(formData.country).length > 100) {
                return 'Le pays doit faire entre 2 et 100 caractères.';
            }
            if (t(formData.zip_code).length < 2 || t(formData.zip_code).length > 100) {
                return 'Le code postal doit faire entre 2 et 100 caractères.';
            }
            return '';
        }
        if (s === 3) {
            const c = t(formData.commission);
            if (c !== '' && (Number.isNaN(Number(c)) || Number(c) < 0)) {
                return 'La commission doit être un nombre positif ou vide.';
            }
            const leg = t(formData.legal_info);
            if (leg.length > 5000) return 'Les informations légales sont trop longues (max. 5000 caractères).';
            const logoUrl = t(formData.logo_url);
            if (logoUrl.length > 2048) {
                return 'L\'URL du logo ne peut pas dépasser 2048 caractères.';
            }
            if (logoUrl !== '') {
                try {
                    // eslint-disable-next-line no-new
                    new URL(logoUrl);
                } catch {
                    return 'L\'URL du logo n\'est pas valide.';
                }
            }
            const contractLink = t(formData.contract_doc_s3_link);
            if (contractLink.length > 2048) {
                return 'Le lien S3 ne peut pas dépasser 2048 caractères.';
            }
            return '';
        }
        return '';
    };

    const handleNext = () => {
        const err = validateStep(step);
        if (err) {
            setPageError(err);
            return;
        }
        setPageError('');
        setStep((x) => Math.min(3, x + 1));
    };

    const handlePrev = () => {
        setPageError('');
        setStep((x) => Math.max(1, x - 1));
    };

    const buildPayload = () => {
        const t = (v) => (v || '').trim();
        const commissionTrim = t(formData.commission);
        return {
            email: t(formData.email) || null,
            city: t(formData.city),
            country: t(formData.country),
            user_address: t(formData.user_address),
            zip_code: t(formData.zip_code),
            first_name: t(formData.first_name),
            last_name: t(formData.last_name),
            phone_number: t(formData.phone_number) || null,
            company_name: t(formData.company_name),
            country_code: t(formData.country_code) || null,
            commission: commissionTrim === '' ? null : Number(commissionTrim),
            legal_info: t(formData.legal_info) || null,
            logo_url: t(formData.logo_url) || null,
            contract_doc_s3_link: t(formData.contract_doc_s3_link) || null,
            role: formData.role,
        };
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        for (const s of [1, 2, 3]) {
            const err = validateStep(s);
            if (err) {
                setPageError(err);
                setStep(s);
                return;
            }
        }
        setPageError('');
        dispatch(createPartnerRequest(buildPayload()));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setPageError('');
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    return (
        <div className="dashboard-page admin-partner-page">
            <Card>
                <Card.Body>
                    <div className="page-header flex-wrap gap-2 mb-4">
                        <div className="d-flex align-items-center gap-3 flex-wrap">
                            <Button variant="outline-secondary" onClick={handleBackToList}>
                                <FiArrowLeft className="me-2" />
                                Retour à la liste
                            </Button>
                            <h2 className="page-title mb-0">
                                <FiLink className="me-2" />
                                Nouveau partenaire
                            </h2>
                        </div>
                    </div>

                    {create.error && (
                        <Alert variant="danger" className="mb-3">
                            {create.error}
                        </Alert>
                    )}

                    {isCreateSuccess ? (
                        <PartnerCreateSuccessResult
                            result={create.data}
                            onBackToList={handleBackToList}
                        />
                    ) : (
                    <Form
                        onSubmit={(e) => {
                            e.preventDefault();
                            if (step === 3) handleSubmit(e);
                        }}
                    >
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="text-muted small">Étape {step} / 3</span>
                                <span className="text-muted small">
                                    {step === 1 && 'Identité et société'}
                                    {step === 2 && 'Adresse'}
                                    {step === 3 && 'Compléments (optionnel)'}
                                </span>
                            </div>
                            <ProgressBar now={(step / 3) * 100} />
                        </div>

                        {pageError && (
                            <Alert variant="danger" className="mb-3" onClose={() => setPageError('')} dismissible>
                                {pageError}
                            </Alert>
                        )}

                        {step === 1 && (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        autoComplete="email"
                                    />
                                </Form.Group>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Prénom *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="first_name"
                                                value={formData.first_name}
                                                onChange={handleChange}
                                                maxLength={100}
                                                autoComplete="given-name"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Nom *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="last_name"
                                                value={formData.last_name}
                                                onChange={handleChange}
                                                maxLength={100}
                                                autoComplete="family-name"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nom de l&apos;entreprise *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="company_name"
                                        value={formData.company_name}
                                        onChange={handleChange}
                                        maxLength={50}
                                    />
                                </Form.Group>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Code pays (indicatif)</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="country_code"
                                                value={formData.country_code}
                                                onChange={handleChange}
                                                placeholder="Ex. 33 — laisser vide pour saisir un numéro en +…"
                                                maxLength={3}
                                            />
                                            <Form.Text className="text-muted">
                                                Optionnel. S’il est renseigné, le téléphone doit être 8 à 15 chiffres sans « + ».
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Rôle *</Form.Label>
                                            <Form.Select name="role" value={formData.role} onChange={handleChange}>
                                                <option value="Partner">Partner</option>
                                                <option value="Orakle">Orakle</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Form.Group className="mb-3">
                                    <Form.Label>Téléphone</Form.Label>
                                    <Form.Control
                                        type="tel"
                                        name="phone_number"
                                        value={formData.phone_number}
                                        onChange={handleChange}
                                        placeholder="Avec code pays : 612345678 · Sans : +33612345678"
                                        autoComplete="tel"
                                    />
                                    <Form.Text className="text-muted">
                                        Optionnel si aucun code pays. Sinon obligatoire : chiffres uniquement. Sans code pays :
                                        « + » puis 8 à 15 chiffres.
                                    </Form.Text>
                                </Form.Group>
                            </>
                        )}

                        {step === 2 && (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label>Adresse *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="user_address"
                                        value={formData.user_address}
                                        onChange={handleChange}
                                        maxLength={100}
                                        placeholder="Numéro et rue"
                                        autoComplete="street-address"
                                    />
                                </Form.Group>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Ville *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="city"
                                                value={formData.city}
                                                onChange={handleChange}
                                                maxLength={100}
                                                autoComplete="address-level2"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Code postal *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="zip_code"
                                                value={formData.zip_code}
                                                onChange={handleChange}
                                                maxLength={100}
                                                autoComplete="postal-code"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Pays *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="country"
                                                value={formData.country}
                                                onChange={handleChange}
                                                maxLength={100}
                                                autoComplete="country-name"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </>
                        )}

                        {step === 3 && (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label>Commission (% ou montant)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="commission"
                                        value={formData.commission}
                                        onChange={handleChange}
                                        min={0}
                                        step="0.01"
                                        placeholder="Laisser vide si non applicable"
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>URL du logo</Form.Label>
                                    <Form.Control
                                        type="url"
                                        name="logo_url"
                                        value={formData.logo_url}
                                        onChange={handleChange}
                                        maxLength={2048}
                                        placeholder="https://exemple.com/logo.png (optionnel)"
                                    />
                                    <Form.Text className="text-muted">
                                        Optionnel. URL du logo partenaire (max. 2048 caractères).
                                    </Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Lien S3 documentation contractuelle</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="contract_doc_s3_link"
                                        value={formData.contract_doc_s3_link}
                                        onChange={handleChange}
                                        maxLength={2048}
                                        placeholder="s3://bucket/chemin/vers/dossier (optionnel)"
                                    />
                                    <Form.Text className="text-muted">
                                        Optionnel. Chemin S3 des documents contractuels du partenaire.
                                    </Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Informations légales</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        name="legal_info"
                                        value={formData.legal_info}
                                        onChange={handleChange}
                                        maxLength={5000}
                                        placeholder="Optionnel"
                                    />
                                </Form.Group>
                            </>
                        )}

                        <div className="d-flex flex-wrap justify-content-between gap-2 mt-4 pt-3 border-top">
                            <div className="d-flex gap-2">
                                <Button variant="outline-secondary" type="button" onClick={handleBackToList} disabled={create.loading}>
                                    Annuler
                                </Button>
                                <Button variant="outline-warning" type="button" onClick={resetForm} disabled={create.loading}>
                                    Réinitialiser le formulaire
                                </Button>
                            </div>
                            <div className="d-flex gap-2">
                                {step > 1 && (
                                    <Button variant="outline-secondary" type="button" onClick={handlePrev} disabled={create.loading}>
                                        Précédent
                                    </Button>
                                )}
                                {step < 3 && (
                                    <Button variant="primary" type="button" onClick={handleNext} disabled={create.loading}>
                                        Suivant
                                    </Button>
                                )}
                                {step === 3 && (
                                    <Button variant="primary" type="submit" disabled={create.loading}>
                                        {create.loading ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Création...
                                            </>
                                        ) : (
                                            'Créer le compte'
                                        )}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </Form>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminPartnerCreate;
